import { requestData } from '../utils/request';
import moment from 'moment-timezone';
import {parseAudioInfoFromEvent, parseChatInfoFromEvent, parsePresentationInfoFromEvent, parseVideoInfoFromEvent, parseWhiteboardInfoFromEvent, parseCursorInfoFromEvent} from '../utils/eventParser'
import { parsePresentationTimeNodes } from '../utils/presentationUtils';

/**
 * 获取回放的基础信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordInfoEvent = async (recordEventUrlTemplate, roomId, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "record.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return data;
}

/**
 * 获取回放的课件信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordPresentationEvent = async (recordEventUrlTemplate, roomId, recordBeginTime, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "presentation.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parsePresentationInfoFromEvent(data, recordBeginTime);
}

/**
 * 获取回放的白板信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordWhiteboardEvent = async (recordEventUrlTemplate, roomId, recordBeginTime, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "whiteboard.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parseWhiteboardInfoFromEvent(data, recordBeginTime);
}

/**
 * 获取回放的课堂聊天信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordChatEvent = async (recordEventUrlTemplate, roomId, recordBeginTime, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "chat.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parseChatInfoFromEvent(data, recordBeginTime);
}


/**
 * 获取回放的课堂聊天信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordCursorEvent = async (recordEventUrlTemplate, roomId, recordBeginTime, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "cursor.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parseCursorInfoFromEvent(data, recordBeginTime);
}

/**
 * 获取回放的音频信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordAudioEvent = async (recordEventUrlTemplate, recordMediaUrlTemplate, roomId, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "audio.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parseAudioInfoFromEvent(data, formatMediaUrlTemplate(recordMediaUrlTemplate, roomId, startTime));
}

/**
 * 获取回放的视频信令信息
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @Param {function} callback - 回调函数，接收数据作为参数
 */
export const getRecordVideoEvent = async (recordEventUrlTemplate, recordMediaUrlTemplate, roomId, startTime) => {

    const url = formatEventUrlTemplate(recordEventUrlTemplate, "video.json", roomId, startTime);

    const result = await requestData(url);

    const data = result?.data;

    return parseVideoInfoFromEvent(data, formatMediaUrlTemplate(recordMediaUrlTemplate, roomId, startTime));
}

/**
 * 获取课件时间节点信息
 * @param {Array} presentationEventData - 课件事件数据
 * @returns {Array} 时间节点数组
 */
export const getPresentationTimeNodes = (presentationEventData) => {
    return parsePresentationTimeNodes(presentationEventData);
}

/**
 * 格式化录制事件的URL模板（使用 moment-timezone 处理东八区时间）
 * @param {string} recordEventUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string} name - 事件名称
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @returns {string} 替换后的完整URL
 */
export const formatEventUrlTemplate = (recordEventUrlTemplate, name, roomId, startTime) => {

  // 使用 moment 解析时间戳，并设置为东八区（Asia/Shanghai）
  const m = moment(startTime).tz('Asia/Shanghai');

  // 格式化 day 为 20250401 形式
  const day = m.format('YYYYMMDD');

  // date 参数：startTime 转为精确到秒的时间戳
  const date = Math.floor(startTime / 1000);

  // 替换模板中的占位符，对 roomId 和 name 进行 URL 编码
  return recordEventUrlTemplate
    .replace(/\{day\}/g, day)
    .replace(/\{roomId\}/g, encodeURIComponent(roomId))
    .replace(/\{name\}/g, encodeURIComponent(name))
    .replace(/\{date\}/g, date);
}

/**
 * 格式化录制事件的URL模板（使用 moment-timezone 处理东八区时间）
 * @param {string} recordMediaUrlTemplate - URL模板，包含 {day} 和 {date} 占位符
 * @param {string} name - 事件名称
 * @param {string|number} roomId - 房间ID
 * @param {number} startTime - 开始时间戳（毫秒）
 * @returns {string} 替换后的完整URL
 */
export const formatMediaUrlTemplate = (recordMediaUrlTemplate, roomId, startTime) => {

    // 使用 moment 解析时间戳，并设置为东八区（Asia/Shanghai）
    const m = moment(startTime).tz('Asia/Shanghai');
  
    // 格式化 day 为 20250401 形式
    const day = m.format('YYYYMMDD');
  
    // date 参数：startTime 转为精确到秒的时间戳
    const date = Math.floor(startTime / 1000);
  
    // 替换模板中的占位符，对 roomId 和 name 进行 URL 编码
    return recordMediaUrlTemplate
      .replace(/\{day\}/g, day)
      .replace(/\{roomId\}/g, encodeURIComponent(roomId))
      .replace(/\{date\}/g, date);
}